import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Layers, CalendarClock, ImageIcon, Upload } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";

export default function AssessmentConfig() {
  const [framework, setFramework] = useState(null);

  const load = () => api.get("/config/framework").then((r) => setFramework(r.data)).catch(() => setFramework([]));
  useEffect(() => { load(); }, []);
  if (!framework) return <Loader label="Loading configuration" />;

  const totalQ = framework.reduce((n, p) => n + p.questions.length, 0);

  return (
    <div>
      <PageHeader
        overline="Administration"
        title="Assessment Configuration"
        description="Define pillars, questions and weights. This drives the scoring engine for every partner assessment."
        actions={<AddPillar onDone={load} />}
      />
      <WindowEditor />
      <BrandingEditor />
      <div className="mb-4 text-sm text-muted-foreground font-mono">{framework.length} pillars · {totalQ} questions</div>
      <Accordion type="multiple" className="space-y-3" defaultValue={framework.map((p) => p.id)}>
        {framework.map((p, i) => (
          <AccordionItem key={p.id} value={p.id} className="bg-card border border-border rounded-md px-4" data-testid={`config-pillar-${i}`}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3 text-left">
                <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <div className="font-heading font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">Weight {p.weight} · {p.questions.length} questions</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-sm text-muted-foreground mb-3">{p.description}</p>
              <ul className="space-y-2 mb-3">
                {p.questions.map((q, qi) => (
                  <li key={q.id} className="flex items-start justify-between gap-3 border border-border/60 rounded-md p-3" data-testid={`config-question-${q.id}`}>
                    <div className="flex items-start gap-2">
                      <span className="font-mono text-xs text-muted-foreground mt-0.5">{qi + 1}.</span>
                      <div>
                        <div className="text-sm">{q.text}</div>
                        <div className="text-xs text-muted-foreground font-mono">max {q.max_score} · weight {q.weight}</div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive h-8" data-testid={`delete-question-${q.id}`}
                      onClick={async () => { await api.delete(`/config/questions/${q.id}`); toast.success("Question archived"); load(); }}>
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
              <AddQuestion pillarId={p.id} onDone={load} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function WindowEditor() {
  const [win, setWin] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/config/window").then((r) => setWin(r.data)).catch(() => setWin({ active: false, open_date: "", close_date: "", message: "" })); }, []);
  if (!win) return null;
  const upd = (k, v) => setWin({ ...win, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config/window", {
        open_date: win.open_date || null, close_date: win.close_date || null,
        active: !!win.active, message: win.message || "",
      });
      toast.success("Assessment window saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mb-6 bg-card border border-border rounded-md p-5" data-testid="window-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="overline flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" aria-hidden="true" /> Assessment submission window</div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{win.active ? "Open" : "Closed"}</span>
          <Switch checked={!!win.active} onCheckedChange={(v) => upd("active", v)} data-testid="window-active-switch" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Opens</Label><Input type="date" value={win.open_date || ""} onChange={(e) => upd("open_date", e.target.value)} className="mt-1 rounded-md" data-testid="window-open-input" /></div>
        <div><Label>Closes</Label><Input type="date" value={win.close_date || ""} onChange={(e) => upd("close_date", e.target.value)} className="mt-1 rounded-md" data-testid="window-close-input" /></div>
      </div>
      <div className="mt-3"><Label>Message to partners</Label><Textarea value={win.message || ""} onChange={(e) => upd("message", e.target.value)} className="mt-1 rounded-md" placeholder="e.g. Please submit your FY2026 assessment before the closing date." data-testid="window-message-input" /></div>
      <div className="mt-3 flex justify-end"><Button onClick={save} disabled={saving} className="rounded-md" data-testid="save-window-button">Save window</Button></div>
    </div>
  );
}

function BrandingEditor() {
  const { logo, favicon, refresh } = useBranding();
  const [lg, setLg] = useState(logo);
  const [fv, setFv] = useState(favicon);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLg(logo); setFv(favicon); }, [logo, favicon]);

  const read = (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_000_000) { toast.error("Image too large (max 2MB)"); return; }
    const r = new FileReader();
    r.onload = () => setter(r.result);
    r.readAsDataURL(file);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config/branding", { logo: lg || null, favicon: fv || null });
      toast.success("Branding saved — applied across the platform");
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb-6 bg-card border border-border rounded-md p-5" data-testid="branding-editor">
      <div className="overline flex items-center gap-2 mb-4"><ImageIcon className="h-3.5 w-3.5" aria-hidden="true" /> Organisation branding</div>
      <p className="text-sm text-muted-foreground mb-4">Upload the official EnterpriseSG logo and favicon. The logo replaces the shield mark in the sidebar and on the sign-in screen.</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs">Logo</Label>
          <div className="mt-2 flex items-center gap-3">
            <div className="h-14 w-40 rounded-md border border-border bg-[#303E49] flex items-center justify-center overflow-hidden">
              {lg ? <img src={lg} alt="logo preview" className="h-12 max-w-[150px] object-contain" data-testid="logo-preview" /> : <span className="text-[11px] text-slate-300 font-mono">no logo</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer rounded-md border border-border px-3 py-1.5 hover:border-slate-400" data-testid="upload-logo-label">
                <Upload className="h-3.5 w-3.5" aria-hidden="true" /> Choose logo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => read(e, setLg)} data-testid="logo-file-input" />
              </label>
              {lg && <button className="text-xs text-destructive hover:underline text-left" onClick={() => setLg(null)} data-testid="remove-logo">Remove</button>}
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs">Favicon</Label>
          <div className="mt-2 flex items-center gap-3">
            <div className="h-14 w-14 rounded-md border border-border bg-muted flex items-center justify-center overflow-hidden">
              {fv ? <img src={fv} alt="favicon preview" className="h-10 w-10 object-contain" data-testid="favicon-preview" /> : <span className="text-[10px] text-muted-foreground font-mono">none</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer rounded-md border border-border px-3 py-1.5 hover:border-slate-400" data-testid="upload-favicon-label">
                <Upload className="h-3.5 w-3.5" aria-hidden="true" /> Choose favicon
                <input type="file" accept="image/*" className="hidden" onChange={(e) => read(e, setFv)} data-testid="favicon-file-input" />
              </label>
              {fv && <button className="text-xs text-destructive hover:underline text-left" onClick={() => setFv(null)} data-testid="remove-favicon">Remove</button>}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-md" data-testid="save-branding-button">Save branding</Button>
      </div>
    </div>
  );
}

function AddPillar({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", weight: 1.0, order: 99 });
  const save = async () => {
    try { await api.post("/config/pillars", form); toast.success("Pillar added"); setOpen(false); setForm({ name: "", description: "", weight: 1.0, order: 99 }); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-md" data-testid="add-pillar-button"><Plus className="h-4 w-4 mr-1.5" aria-hidden="true" /> Add pillar</Button></DialogTrigger>
      <DialogContent className="rounded-md">
        <DialogHeader><DialogTitle className="font-heading">Add pillar</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-md" data-testid="pillar-name-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-md" /></div>
          <div><Label>Weight</Label><Input type="number" step="0.1" value={form.weight} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })} className="mt-1 rounded-md" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.name} className="rounded-md" data-testid="confirm-add-pillar">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddQuestion({ pillarId, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ text: "", guidance: "Rate maturity from 0 to 5.", max_score: 5, weight: 1.0, order: 99 });
  const save = async () => {
    try { await api.post("/config/questions", { pillar_id: pillarId, ...form }); toast.success("Question added"); setOpen(false); setForm({ text: "", guidance: "Rate maturity from 0 to 5.", max_score: 5, weight: 1.0, order: 99 }); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-md" data-testid={`add-question-button-${pillarId}`}><Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Add question</Button></DialogTrigger>
      <DialogContent className="rounded-md">
        <DialogHeader><DialogTitle className="font-heading">Add question</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Question text</Label><Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="mt-1 rounded-md" data-testid="question-text-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Max score</Label><Input type="number" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: Number(e.target.value) })} className="mt-1 rounded-md" /></div>
            <div><Label>Weight</Label><Input type="number" step="0.1" value={form.weight} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })} className="mt-1 rounded-md" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.text} className="rounded-md" data-testid="confirm-add-question">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
